import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getBrowserStorage, loadShigoSession } from '../../../lib/sessionPersistence';
import { parseRuntimeNetworkBrandDetails } from '../../../lib/networkBranding';
import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createNetworksClient } from './api';
import type { NetworkBrandAssetFiles, NetworkMutationPayload, NetworksFilter } from './model';

export const networksQueryKey = ['admin', 'networks'] as const;
export const networksListsQueryKey = [...networksQueryKey, 'list'] as const;

function useNetworksClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createNetworksClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useNetworksQuery(filter: NetworksFilter) {
  const client = useNetworksClient();
  return useQuery({
    queryKey: [...networksListsQueryKey, filter],
    queryFn: () => client.list(filter),
    enabled: filter.statuses.length > 0,
  });
}

export function useNetworkQuery(id?: string) {
  const client = useNetworksClient();
  return useQuery({
    queryKey: [...networksQueryKey, 'detail', id],
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useNetworkMutations() {
  const { session, setSession } = useShigoSession();
  const { networkId } = readShigoAppConfig();
  const client = useNetworksClient();
  const queryClient = useQueryClient();
  const invalidateLists = () => queryClient.invalidateQueries({ queryKey: networksListsQueryKey });
  return {
    create: useMutation({
      mutationFn: ({
        payload,
        assets,
      }: {
        payload: NetworkMutationPayload;
        assets: NetworkBrandAssetFiles;
      }) => client.create(payload, assets),
      onSuccess: invalidateLists,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        payload,
        assets,
      }: {
        id: string;
        payload: NetworkMutationPayload;
        assets: NetworkBrandAssetFiles;
      }) => client.update(id, payload, assets),
      onMutate: () => session?.accessToken,
      onSuccess: async (network, _variables, requestToken) => {
        const storage = getBrowserStorage();
        const latest = storage ? loadShigoSession(storage) : null;
        if (network?.id === networkId && latest && latest.accessToken === requestToken) {
          setSession({
            ...latest,
            user: {
              ...latest.user,
              networkBrandDetails: parseRuntimeNetworkBrandDetails(network.brandDetails) ?? {},
            },
          });
        }
        await invalidateLists();
      },
    }),
    setStatus: useMutation({
      mutationFn: ({ id, active }: { id: string; active: boolean }) => client.setStatus(id, active),
      onSuccess: invalidateLists,
    }),
  };
}

export function useNetworkStatusCountsQuery(searchTerm: string) {
  const client = useNetworksClient();
  const search = searchTerm.trim();
  return useQuery({
    queryKey: [...networksListsQueryKey, { statusCounts: true }, search],
    queryFn: () => client.getStatusCounts(search),
  });
}
